import { useState, useEffect, useCallback, useMemo, Fragment } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import { authFetch } from '../../lib/auth-fetch';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Package, AlertTriangle, Plus, Trash2, ClipboardCheck,
  Search, Edit2, Save, X, FileDown, Receipt, Mail, MailX,
} from 'lucide-react';
import { toast } from 'sonner';
import DeliveryNoteUploadDialog from '../../components/inventory/DeliveryNoteUploadDialog';
import { localYMD } from '../../lib/dates';

export default function InventoryPage() {
  const { user } = useAuth();
  const { can } = usePermissions();
  const [items, setItems] = useState([]);
  const [totalValue, setTotalValue] = useState(0);
  const [orphanItemIds, setOrphanItemIds] = useState(new Set());
  const [showInvoiceDialog, setShowInvoiceDialog] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('list');  // 'list' | 'inventur' | 'low'
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [newItem, setNewItem] = useState({
    hersteller: '', modell: '', stock: 0, minStock: 0, purchasePriceEur: 0,
  });
  const [inventurMap, setInventurMap] = useState({}); // itemId → newStock
  const canEdit = can('inventory.update');
  const canAdjust = can('inventory.adjust');
  const canCreate = can('inventory.create');
  const canDelete = can('inventory.delete');

  // Lieferschein-Weiterleitung an order@ees-energiespeicher.de (Toggle
  // 28.02.2026). Nur Admin + Innendienst dürfen umschalten, aber der
  // Status wird für alle mit `inventory.read` gelesen — der Toggle-Knopf
  // ist trotzdem nur für die beiden Rollen sichtbar.
  const canToggleMailForward = ['admin', 'innendienst'].includes(user?.role);
  const [mailForwardEnabled, setMailForwardEnabled] = useState(null);
  const [togglingMailForward, setTogglingMailForward] = useState(false);

  const loadMailForward = useCallback(async () => {
    const r = await authFetch('/api/inventory/delivery-notes/mail-forward-setting');
    if (r.ok) {
      const d = await r.json();
      setMailForwardEnabled(!!d.enabled);
    }
  }, []);

  useEffect(() => { loadMailForward(); }, [loadMailForward]);

  const toggleMailForward = async () => {
    setTogglingMailForward(true);
    const next = !mailForwardEnabled;
    const r = await authFetch('/api/inventory/delivery-notes/mail-forward-setting', {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ enabled: next }),
    });
    setTogglingMailForward(false);
    if (r.ok) {
      const d = await r.json();
      setMailForwardEnabled(!!d.enabled);
      toast.success(d.enabled
        ? 'Lieferschein-Versand aktiviert — komprimierte PDFs gehen an order@ees-energiespeicher.de.'
        : 'Lieferschein-Versand deaktiviert.');
    } else {
      const d = await r.json().catch(() => ({}));
      toast.error(d.detail || 'Einstellung konnte nicht geändert werden');
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    const r = await authFetch('/api/inventory');
    if (r.ok) {
      const d = await r.json();
      setItems(d.items || []);
      setTotalValue(d.totalInventoryValueEur || 0);
    }
    // Welche Lager-Artikel sind keinem System als Komponente zugeordnet?
    // → Frontend zeigt ein gelbes Warndreieck zur Aufmerksamkeit.
    // WICHTIG: Quelle ist die SYSTEM-DATENBANK (`/api/systems`), nicht der
    // PV-Konfigurator-Pool — letzterer enthält keine `components`.
    try {
      const r2 = await authFetch('/api/systems');
      if (r2.ok) {
        const cfg = await r2.json();
        const usedKeys = new Set();
        for (const s of cfg.systems || []) {
          for (const c of s.components || []) {
            // Match-Logik identisch zu inventory.py `_normalize_key`:
            // Lowercase + getrimmt — keine Sonderzeichen entfernen, damit
            // exakt der gespeicherte `key`-Feldwert getroffen wird.
            const h = (c.hersteller || '').toLowerCase().trim();
            const m = (c.modell || '').toLowerCase().trim();
            if (m) usedKeys.add(`${h}|${m}`);
          }
        }
        const orphans = new Set();
        for (const it of (await (await authFetch('/api/inventory')).json()).items || []) {
          const k = `${(it.hersteller || '').toLowerCase().trim()}|${(it.modell || '').toLowerCase().trim()}`;
          if (!usedKeys.has(k)) orphans.add(it.id);
        }
        setOrphanItemIds(orphans);
      }
    } catch { /* silent */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    let list = items;
    if (tab === 'low') {
      list = list.filter(i => (i.minStock || 0) > 0 && (i.stock || 0) < i.minStock);
    }
    if (needle) {
      list = list.filter(i =>
        (i.hersteller || '').toLowerCase().includes(needle) ||
        (i.modell || '').toLowerCase().includes(needle),
      );
    }
    return list;
  }, [items, search, tab]);

  const lowCount = useMemo(() =>
    items.filter(i => (i.minStock || 0) > 0 && (i.stock || 0) < i.minStock).length,
  [items]);

  const createItem = async () => {
    if (!newItem.modell.trim()) return;
    const r = await authFetch('/api/inventory', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        hersteller: newItem.hersteller, modell: newItem.modell,
        stock: Number(newItem.stock) || 0, minStock: Number(newItem.minStock) || 0,
        purchasePriceEur: Number(newItem.purchasePriceEur) || 0,
      }),
    });
    if (r.ok) {
      setShowNew(false);
      setNewItem({ hersteller: '', modell: '', stock: 0, minStock: 0, purchasePriceEur: 0 });
      await load();
    } else { const d = await r.json().catch(() => ({})); alert(d.detail || 'Fehler'); }
  };

  const saveEdit = async () => {
    if (!editing) return;
    // Noch nicht mit Enter bestätigten Alias-Text beim Speichern mitübernehmen
    // (User-Report 18.06.26: Eintippen + direkt Speichern ging sonst verloren).
    const pending = (editing._aliasInput || '').trim();
    const aliasesList = [...(Array.isArray(editing.aliases) ? editing.aliases : [])];
    if (pending && !aliasesList.some(a => a.toLowerCase() === pending.toLowerCase())) {
      aliasesList.push(pending);
    }
    const r = await authFetch(`/api/inventory/${editing.id}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        hersteller: editing.hersteller, modell: editing.modell,
        minStock: Number(editing.minStock) || 0,
        purchasePriceEur: editing.purchasePriceEur != null
          ? Number(editing.purchasePriceEur) || 0 : undefined,
        notes: editing.notes || '',
        aliases: aliasesList,
      }),
    });
    if (r.ok) {
      // Kein Full-Reload — nur den geänderten Artikel im State ersetzen,
      // damit der User nicht zum Listen-Anfang springt (User-Report 27.08.).
      const updated = await r.json();
      setItems(prev => prev.map(x => x.id === updated.id ? { ...x, ...updated } : x));
      setEditing(null);
    } else { const d = await r.json().catch(() => ({})); alert(d.detail || 'Fehler'); }
  };

  const deleteItem = async (id) => {
    if (!window.confirm('Diesen Artikel wirklich aus dem Lager entfernen?')) return;
    const r = await authFetch(`/api/inventory/${id}`, { method: 'DELETE' });
    if (r.ok) setItems(prev => prev.filter(x => x.id !== id));
    else alert('Löschen fehlgeschlagen');
  };

  const setInventur = async (item, newStock) => {
    const reason = prompt(`Inventur für "${item.modell}":\nAktuell ${item.stock}, neuer Bestand?`, String(newStock));
    if (reason === null) return;
    const val = Number(reason);
    if (Number.isNaN(val)) { alert('Ungültige Zahl'); return; }
    const r = await authFetch(`/api/inventory/${item.id}/inventur`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ newStock: val, reason: 'Inventur per Dashboard' }),
    });
    if (r.ok) {
      const upd = await r.json();
      setInventurMap({});
      setItems(prev => prev.map(x => x.id === upd.id ? { ...x, ...upd } : x));
    } else alert('Fehler bei Inventur');
  };

  const adjust = async (item, delta) => {
    const r = await authFetch(`/api/inventory/${item.id}/adjust`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        delta, type: delta > 0 ? 'manual_add' : 'manual_remove',
        reason: delta > 0 ? 'Wareneingang' : 'Manueller Abzug',
      }),
    });
    if (r.ok) {
      const upd = await r.json();
      setItems(prev => prev.map(x => x.id === upd.id ? { ...x, ...upd } : x));
    }
  };

  return (
    <div className="p-6 max-w-7xl">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl font-bold text-[#32373c] flex items-center gap-2" data-testid="inventory-title">
            <Package className="w-5 h-5 text-[#9ec043]" /> Lagerverwaltung
          </h1>
          <p className="text-xs text-gray-400">Komponenten-Bestände, Inventur, Mindestbestand-Warnungen</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="px-3 py-1.5 rounded-lg bg-emerald-50 border border-emerald-200 text-right"
               data-testid="inventory-total-value">
            <div className="text-[10px] uppercase tracking-wide text-emerald-700">Lagerbestand</div>
            <div className="text-sm font-bold text-emerald-800 tabular-nums">
              {totalValue.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
            </div>
          </div>
          <Button variant="outline" onClick={async () => {
            const r = await authFetch('/api/inventory/_/inventory-xlsx');
            if (!r.ok) { alert('Excel-Export fehlgeschlagen'); return; }
            const blob = await r.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Inventur_${localYMD(new Date())}.xlsx`;
            a.click();
            setTimeout(() => URL.revokeObjectURL(url), 1000);
          }} className="text-sm" data-testid="btn-inventory-xlsx">
            <FileDown className="w-4 h-4 mr-1" /> Inventur-Excel
          </Button>
          {canCreate && (
            <Button onClick={() => setShowInvoiceDialog(true)} variant="outline" className="text-sm" data-testid="btn-delivery-note-import">
              <Receipt className="w-4 h-4 mr-1" /> Lieferschein importieren
            </Button>
          )}
          {canToggleMailForward && mailForwardEnabled !== null && (
            <Button
              variant="outline"
              onClick={toggleMailForward}
              disabled={togglingMailForward}
              className={`text-sm ${mailForwardEnabled
                ? 'text-emerald-700 border-emerald-300 hover:bg-emerald-50'
                : 'text-gray-500 border-gray-300 hover:bg-gray-50'}`}
              title={mailForwardEnabled
                ? 'Weiterleitung an order@ees-energiespeicher.de ist AKTIV — Klick zum Deaktivieren'
                : 'Weiterleitung an order@ees-energiespeicher.de ist AUS — Klick zum Aktivieren'}
              data-testid="btn-delivery-note-mail-toggle"
            >
              {mailForwardEnabled ? <Mail className="w-4 h-4 mr-1" /> : <MailX className="w-4 h-4 mr-1" />}
              Versand: {mailForwardEnabled ? 'An' : 'Aus'}
            </Button>
          )}
          {canCreate && (
            <Button onClick={() => setShowNew(true)} className="bg-[#9ec043] hover:bg-[#8aad38] text-white text-sm" data-testid="btn-new-item">
              <Plus className="w-4 h-4 mr-1" /> Artikel anlegen
            </Button>
          )}
        </div>
      </div>

      {showInvoiceDialog && (
        <DeliveryNoteUploadDialog
          onClose={() => setShowInvoiceDialog(false)}
          onDone={() => { setShowInvoiceDialog(false); load(); }}
        />
      )}

      {/* Tabs + Search */}
      <div className="flex flex-wrap gap-2 items-center mb-4">
        <div className="inline-flex rounded-lg border border-gray-200 p-0.5 bg-gray-50">
          {[
            { id: 'list', label: `Alle (${items.length})` },
            { id: 'low', label: `Unter Mindestbestand (${lowCount})` },
          ].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`text-xs px-3 py-1 rounded ${tab === t.id ? 'bg-white shadow-sm text-[#32373c] font-medium' : 'text-gray-500 hover:text-gray-700'}`}
              data-testid={`inv-tab-${t.id}`}>
              {t.label}
            </button>
          ))}
        </div>
        <div className="relative flex-1 max-w-md">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-gray-400" />
          <Input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Hersteller oder Modell suchen…"
            className="pl-7 text-sm h-9" data-testid="inv-search" />
        </div>
      </div>

      {showNew && (
        <div className="bg-white border border-gray-200 rounded-xl p-4 mb-4" data-testid="new-item-form">
          <h3 className="text-sm font-semibold text-[#32373c] mb-3">Neuer Lagerartikel</h3>
          <div className="grid grid-cols-5 gap-3">
            <Input value={newItem.hersteller} onChange={e => setNewItem(p => ({ ...p, hersteller: e.target.value }))}
              placeholder="Hersteller" className="text-sm" data-testid="new-item-hersteller" />
            <Input value={newItem.modell} onChange={e => setNewItem(p => ({ ...p, modell: e.target.value }))}
              placeholder="Modell *" className="text-sm" data-testid="new-item-modell" />
            <Input type="number" value={newItem.stock} onChange={e => setNewItem(p => ({ ...p, stock: e.target.value }))}
              placeholder="Bestand" className="text-sm" data-testid="new-item-stock" />
            <Input type="number" value={newItem.minStock} onChange={e => setNewItem(p => ({ ...p, minStock: e.target.value }))}
              placeholder="Mindestbestand" className="text-sm" data-testid="new-item-minstock" />
            <Input type="number" step="0.01" value={newItem.purchasePriceEur}
              onChange={e => setNewItem(p => ({ ...p, purchasePriceEur: e.target.value }))}
              placeholder="EK-Preis €/Stk" className="text-sm" data-testid="new-item-price" />
          </div>
          <div className="flex gap-2 mt-3">
            <Button onClick={createItem} className="bg-[#9ec043] hover:bg-[#8aad38] text-white text-sm" data-testid="btn-create-item">Anlegen</Button>
            <Button variant="outline" onClick={() => setShowNew(false)} className="text-sm">Abbrechen</Button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-xs text-gray-400">Lade Lager…</div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="inventory-table">
              <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-gray-400">
                <tr>
                  <th className="text-left px-4 py-2">Hersteller</th>
                  <th className="text-left px-4 py-2">Modell</th>
                  <th className="text-right px-4 py-2 w-24">Bestand gebucht</th>
                  <th className="text-right px-4 py-2 w-24">Verfügbar</th>
                  <th className="text-right px-4 py-2 w-28">Mindest­bestand</th>
                  <th className="text-right px-4 py-2 w-28">EK-Preis €/Stk</th>
                  <th className="text-right px-4 py-2 w-32">Gesamtwert €</th>
                  <th className="text-left px-4 py-2 w-48">Status</th>
                  {canEdit && <th className="px-4 py-2 w-48 text-right">Aktionen</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map(item => {
                  const low = (item.minStock || 0) > 0 && (item.stock || 0) < item.minStock;
                  const isEdit = editing?.id === item.id;
                  return (
                    <Fragment key={item.id}>
                    <tr className={low ? 'bg-red-50/40' : ''} data-testid={`inv-row-${item.id}`}>
                      {isEdit ? (
                        <>
                          <td className="px-4 py-2"><Input value={editing.hersteller || ''} onChange={e => setEditing(p => ({ ...p, hersteller: e.target.value }))} className="text-sm h-8 border-gray-200" /></td>
                          <td className="px-4 py-2 text-xs text-gray-400 italic">↓ Vollständige Bearbeitung unten</td>
                          <td className="px-4 py-2 text-right tabular-nums text-gray-400">{item.stock}</td>
                          <td className="px-4 py-2 text-right tabular-nums text-gray-400">—</td>
                          <td className="px-4 py-2 text-right"><Input type="number" value={editing.minStock ?? 0} onChange={e => setEditing(p => ({ ...p, minStock: e.target.value }))} className="text-sm h-8 text-right tabular-nums border-gray-200 w-20 ml-auto" data-testid={`inv-edit-minstock-${item.id}`} /></td>
                          <td className="px-4 py-2 text-right"><Input type="number" step="0.01" value={editing.purchasePriceEur ?? 0} onChange={e => setEditing(p => ({ ...p, purchasePriceEur: e.target.value }))} className="text-sm h-8 text-right tabular-nums border-gray-200 w-24 ml-auto" data-testid={`inv-edit-price-${item.id}`} /></td>
                          <td className="px-4 py-2 text-right tabular-nums text-gray-400">
                            {((Number(editing.purchasePriceEur) || 0) * (item.stock || 0)).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                          <td className="px-4 py-2 text-[10px] text-gray-400">—</td>
                          <td className="px-4 py-2 text-right">
                            <button onClick={saveEdit} className="p-1.5 text-green-600 hover:bg-green-50 rounded" data-testid={`btn-save-inv-${item.id}`}><Save className="w-3.5 h-3.5" /></button>
                            <button onClick={() => setEditing(null)} className="p-1.5 text-gray-400 hover:bg-gray-100 rounded ml-1"><X className="w-3.5 h-3.5" /></button>
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="px-4 py-2 text-gray-600">
                            {orphanItemIds.has(item.id) && (
                              <span title="Keinem System als Komponente zugeordnet — bitte prüfen"
                                    className="inline-block mr-1.5 text-amber-500"
                                    data-testid={`inv-orphan-warning-${item.id}`}>
                                <AlertTriangle className="w-3.5 h-3.5 inline" />
                              </span>
                            )}
                            {item.hersteller || <span className="text-gray-300 italic">—</span>}
                          </td>
                          <td className="px-4 py-2 text-[#32373c] font-medium">{item.modell}</td>
                          <td className={`px-4 py-2 text-right tabular-nums font-semibold ${low ? 'text-red-600' : 'text-[#32373c]'}`} data-testid={`inv-stock-booked-${item.id}`}>{item.stock}</td>
                          <td className="px-4 py-2 text-right tabular-nums" data-testid={`inv-stock-available-${item.id}`}>
                            {(() => {
                              const reserved = Number(item.reservedStock || 0);
                              const available = (Number(item.stock) || 0) - reserved;
                              if (reserved === 0) {
                                return <span className="text-gray-500">{available}</span>;
                              }
                              if (available < 0) {
                                return <span className="inline-flex items-center gap-1 text-red-700 bg-red-100 px-2 py-0.5 rounded"
                                             title={`Reserviert: ${reserved}`}>
                                  {available} <span className="text-[10px]">überzogen</span>
                                </span>;
                              }
                              return <span className="text-amber-700" title={`Reserviert: ${reserved}`}>{available}</span>;
                            })()}
                          </td>
                          <td className="px-4 py-2 text-right tabular-nums text-gray-500">{item.minStock || 0}</td>
                          <td className="px-4 py-2 text-right tabular-nums text-gray-700">
                            {(Number(item.purchasePriceEur) || 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                          <td className="px-4 py-2 text-right tabular-nums font-semibold text-emerald-700">
                            {(Number(item.totalValueEur) || 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                          <td className="px-4 py-2">
                            {low ? (
                              <span className="inline-flex items-center gap-1 text-[11px] text-red-600 font-medium">
                                <AlertTriangle className="w-3 h-3" /> Unter Mindestbestand
                              </span>
                            ) : (item.stock || 0) === 0 ? (
                              <span className="text-[11px] text-gray-400">Leer</span>
                            ) : (
                              <span className="text-[11px] text-green-600">OK</span>
                            )}
                          </td>
                          {canEdit && (
                            <td className="px-4 py-2 text-right whitespace-nowrap">
                              <button onClick={() => adjust(item, 1)} title="+1" className="px-1.5 py-0.5 text-xs text-green-600 hover:bg-green-50 rounded" data-testid={`btn-inv-plus-${item.id}`}>+1</button>
                              <button onClick={() => adjust(item, -1)} title="-1" className="px-1.5 py-0.5 text-xs text-orange-500 hover:bg-orange-50 rounded ml-0.5" data-testid={`btn-inv-minus-${item.id}`}>−1</button>
                              <button onClick={() => setInventur(item, item.stock)} title="Inventur" className="p-1.5 text-gray-400 hover:text-[#9ec043] rounded ml-1" data-testid={`btn-inv-inventur-${item.id}`}>
                                <ClipboardCheck className="w-3.5 h-3.5" />
                              </button>
                              <button onClick={() => setEditing({ ...item })} className="p-1.5 text-gray-400 hover:text-[#9ec043] rounded ml-1" data-testid={`btn-inv-edit-${item.id}`}>
                                <Edit2 className="w-3.5 h-3.5" />
                              </button>
                              {canDelete && (
                                <button onClick={() => deleteItem(item.id)} className="p-1.5 text-gray-300 hover:text-red-500 rounded ml-1" data-testid={`btn-inv-del-${item.id}`}>
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </td>
                          )}
                        </>
                      )}
                    </tr>
                    {isEdit && (
                      <tr className="bg-emerald-50/40 border-t border-emerald-100"
                          data-testid={`inv-edit-expand-${item.id}`}>
                        <td colSpan={canEdit ? 9 : 8} className="px-4 py-3">
                          <div className="space-y-2">
                            <div>
                              <label className="text-[10px] uppercase tracking-wide text-gray-500 font-medium block mb-1">
                                Modell (Volltext-Bezeichnung)
                              </label>
                              <textarea
                                value={editing.modell || ''}
                                onChange={(e) => setEditing(p => ({ ...p, modell: e.target.value }))}
                                rows={2}
                                className="w-full text-sm border border-gray-200 rounded px-3 py-2 focus:border-[#9ec043] focus:outline-none font-mono"
                                placeholder="z. B. Leitungsschutzschalter B32A 1polig"
                                data-testid={`inv-edit-modell-large-${item.id}`}
                              />
                            </div>
                            <div>
                              <label className="text-[10px] uppercase tracking-wide text-gray-500 font-medium block mb-1">
                                Zuordnung / Alias (EasyBill-Nr., -Bezeichnung oder PBD-Option)
                              </label>
                              <div className="flex flex-wrap gap-1 mb-1.5" data-testid={`inv-edit-aliases-${item.id}`}>
                                {(editing.aliases || []).length === 0 && (
                                  <span className="text-[11px] text-gray-400 italic">Noch keine Zuordnung — Bestellpositionen &amp; angehakte PBD-Optionen matchen hierüber.</span>
                                )}
                                {(editing.aliases || []).map((al, ai) => (
                                  <span key={ai} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#eef6d8] text-[#55701f] text-[11px] border border-[#cfe39a]">
                                    {al}
                                    <button
                                      onClick={() => setEditing(p => ({ ...p, aliases: (p.aliases || []).filter((_, k) => k !== ai) }))}
                                      className="text-[#8aa441] hover:text-red-600"
                                      data-testid={`inv-edit-alias-remove-${item.id}-${ai}`}
                                    >&times;</button>
                                  </span>
                                ))}
                              </div>
                              <input
                                type="text"
                                value={editing._aliasInput || ''}
                                onChange={(e) => setEditing(p => ({ ...p, _aliasInput: e.target.value }))}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    const v = (editing._aliasInput || '').trim();
                                    if (!v) return;
                                    setEditing(p => ({
                                      ...p,
                                      aliases: [...(p.aliases || []), v],
                                      _aliasInput: '',
                                    }));
                                  }
                                }}
                                className="w-full text-xs border border-gray-200 rounded px-3 py-2 focus:border-[#9ec043] focus:outline-none"
                                placeholder="EasyBill-Nr. oder -Bezeichnung eintragen, Enter zum Hinzufügen"
                                data-testid={`inv-edit-alias-input-${item.id}`}
                              />
                            </div>
                            <div>
                              <label className="text-[10px] uppercase tracking-wide text-gray-500 font-medium block mb-1">
                                Notizen (optional)
                              </label>
                              <textarea
                                value={editing.notes || ''}
                                onChange={(e) => setEditing(p => ({ ...p, notes: e.target.value }))}
                                rows={2}
                                className="w-full text-xs border border-gray-200 rounded px-3 py-2 focus:border-[#9ec043] focus:outline-none"
                                placeholder="Interne Notiz zum Artikel (z. B. Lieferantenwechsel, Alternativ-Modelle …)"
                                data-testid={`inv-edit-notes-${item.id}`}
                              />
                            </div>
                            <div className="text-[10px] text-gray-400 italic">
                              Änderungen werden lokal gespeichert — nach dem Klick auf ✓ (Speichern) bleibst du an dieser Position in der Liste.
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                    </Fragment>
                  );
                })}
                {filtered.length === 0 && (
                  <tr><td colSpan={canEdit ? 8 : 7} className="px-4 py-10 text-center text-xs text-gray-400">
                    {tab === 'low' ? 'Keine Artikel unter Mindestbestand. 🎉' : 'Keine Einträge.'}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
